import type { Middleware } from "openapi-fetch";
import { ApiError, isApiError } from "../errors/ApiError";
import type { TokenProvider } from "../auth-bridge/tokenProvider";
import { createSingleFlightRefresh } from "../auth-bridge/refreshBridge";

/**
 * Refresh failures that say nothing about the session, thrown as distinct
 * typed errors by `@linkhr/auth`'s `performRefresh`: HTTP 429
 * (`REFRESH_RATE_LIMITED`, the gateway throttled us), a request that got no
 * answer at all (`REFRESH_NETWORK_ERROR`, offline or a dropped connection) and
 * a 5xx (`REFRESH_SERVER_ERROR`, the backend failing or restarting). All are
 * TRANSIENT, so none may trigger a logout: only a refusal (401/403) does.
 * Duck-typed by the stable code string (api-client can't import
 * `@linkhr/auth`: dependency cycle).
 */
function isRefreshTransient(x: unknown): boolean {
  if (typeof x !== "object" || x === null) return false;
  const code = (x as { errorCode?: unknown }).errorCode;
  return (
    code === "REFRESH_RATE_LIMITED" ||
    code === "REFRESH_NETWORK_ERROR" ||
    code === "REFRESH_SERVER_ERROR"
  );
}

/**
 * Rebuild the `onAuthFailure` cause from a failed refresh, PRESERVING the
 * backend's `errorCode`.
 *
 * `performRefresh` throws a plain (non-`ApiError`) object carrying
 * `{errorCode, status, title}`. Hardcoding `AUTH_TOKEN_EXPIRED` here erased
 * that: a demotion (`403 AUTH_ADMIN_AUDIENCE_NOT_PERMITTED`, be-788 — the admin
 * lost their admin-capable role mid-session) arrived at the auth store looking
 * exactly like an expired session, so the app could only offer "sign in again"
 * on an account that can never sign in here. Duck-typed because api-client
 * cannot import `@linkhr/auth` (dependency cycle).
 */
function authFailureCause(refreshErr: unknown, correlationId: string): ApiError {
  const f = (typeof refreshErr === "object" && refreshErr !== null ? refreshErr : {}) as {
    errorCode?: unknown;
    status?: unknown;
    title?: unknown;
  };
  return new ApiError({
    // The value is the backend's wire code. `ErrorCode` is generated from
    // openapi.json, which doesn't enumerate the auth-refresh sentinels
    // (REFRESH_EXPIRED / REFRESH_FAMILY_REVOKED) — hence the cast.
    errorCode:
      typeof f.errorCode === "string"
        ? (f.errorCode as ApiError["errorCode"])
        : "AUTH_TOKEN_EXPIRED",
    title: typeof f.title === "string" ? f.title : "Session expired. Please sign in again.",
    status: typeof f.status === "number" ? f.status : 401,
    correlationId,
  });
}

/**
 * 401 → single-flight refresh → retry seam (D-12).
 *
 * Flow:
 *   1. `errorMiddleware` throws `ApiError` on any non-2xx response (runs first).
 *   2. `wrap(fn)` catches that throw. If status === 401 and errorCode is not
 *      `REFRESH_EXPIRED` (which means the refresh endpoint itself returned 401
 *      — unrecoverable), it awaits the in-tab single-flight refresh.
 *   3. On refresh success: retry the original call once. The `authMiddleware`
 *      re-reads the new access token on retry so no explicit token passing is
 *      needed.
 *   4. On refresh failure: call `tokenProvider.onAuthFailure(cause)` so
 *      `@linkhr/auth` can clear state + redirect to `/login`.
 *
 * Cross-tab dedup lives in `@linkhr/auth` via `BroadcastChannel("auth")` +
 * `navigator.locks.request("refresh", ...)` — this middleware only coordinates
 * within the current tab.
 *
 * Implementation note: openapi-fetch v0.17's `Middleware.onRequest`/`onResponse`
 * cannot retry a request on their own. The retry logic therefore lives in a
 * client-method wrapper produced by `wrap(fn)` — applied at the `client.ts`
 * level when building the public api.
 */
export function createRefreshMiddleware(tokenProvider: TokenProvider): {
  /**
   * Placeholder `Middleware` object. Not strictly needed today — exposed so future
   * openapi-fetch versions (with retry support inside `onResponse`) can fold the
   * logic here without breaking callers.
   */
  middleware: Middleware;
  /**
   * Wraps any async function (typically a typed openapi-fetch client method) so
   * that a thrown 401 `ApiError` triggers one single-flight refresh + retry.
   */
  wrap: <TArgs extends readonly unknown[], TResult>(
    fn: (...args: TArgs) => Promise<TResult>,
  ) => (...args: TArgs) => Promise<TResult>;
} {
  const refresh = createSingleFlightRefresh(tokenProvider);

  // No-op middleware today — the retry lives in `wrap` (openapi-fetch v0.17 cannot
  // retry from inside a middleware callback).
  const middleware: Middleware = {
    onRequest: () => undefined,
  };

  function wrap<TArgs extends readonly unknown[], TResult>(
    fn: (...args: TArgs) => Promise<TResult>,
  ): (...args: TArgs) => Promise<TResult> {
    return async (...args: TArgs): Promise<TResult> => {
      try {
        return await fn(...args);
      } catch (e) {
        if (!isApiError(e)) throw e;
        // A temporary-password session: refreshing mints another restricted
        // token, so hand it to the app, which routes to the password change.
        if (e.status === 403 && e.errorCode === "AUTH_PASSWORD_CHANGE_REQUIRED") {
          tokenProvider.onPasswordChangeRequired?.();
          throw e;
        }
        if (e.status !== 401) throw e;
        // `REFRESH_EXPIRED` = the refresh endpoint itself returned 401 (family
        // revoked / expired). Don't recurse — surface immediately.
        if (e.errorCode === ("REFRESH_EXPIRED" as typeof e.errorCode)) throw e;
        // Fresh-auth step-up 401s are NOT session expiry — the access token is
        // valid; the gateway is demanding (or rejecting) proof of a recent
        // factor. Refreshing here does nothing useful:
        //   - STEP_UP_REQUIRED: the guarded call needs an elevation token first.
        //     Surface it so the caller can mint one and retry with X-Step-Up-Token.
        //   - STEP_UP_FAILED: the step-up MINT itself rejected the proof (wrong
        //     password/TOTP). A refresh-then-retry would silently re-submit the
        //     same bad credential — burning the per-principal failed-attempt
        //     budget twice as fast and masking the real error.
        // (Security boundary: these must stay distinct from session-expiry 401s.)
        if (e.errorCode === "STEP_UP_REQUIRED" || e.errorCode === "STEP_UP_FAILED") throw e;
        // Same for a wrong authenticator / recovery code (turning two-step
        // sign-in on or off): the session is fine, the code isn't, and a retry
        // would spend a second guess from the throttle budget.
        if (e.errorCode === "TOTP_INVALID_CODE" || e.errorCode === "TOTP_RECOVERY_CODE_INVALID") {
          throw e;
        }

        try {
          await refresh();
        } catch (refreshErr) {
          // A throttled (429), unanswered (network) or failed (5xx) refresh is
          // transient: the session is not known to be gone. Do NOT log the user
          // out: surface the original request's 401 through normal error
          // handling; the auth layer retries the refresh with backoff and the
          // next 401 refreshes once things recover.
          if (isRefreshTransient(refreshErr)) throw e;
          const cause = isApiError(refreshErr)
            ? refreshErr
            : authFailureCause(refreshErr, e.correlationId);
          tokenProvider.onAuthFailure(cause);
          throw refreshErr;
        }

        // Retry once with the new token (authMiddleware re-reads it on request).
        return await fn(...args);
      }
    };
  }

  return { middleware, wrap };
}
